import { Button, Spinner } from '@arcadiapower/design-system';
import { useState, useMemo, useCallback } from 'react';
import { ApiMode, Organization, Provider, UseCase } from '@arc-connect/schema';
import { trpc } from '@client/utils/trpc';
import { Trans, useTranslation } from 'react-i18next';
import { Form } from '@client/components/form';
import SVG from 'react-inlinesvg';
import arcadiaBadge from 'packages/client/src/assets/icons/arcadia-badge.svg';
import { ContentSectionFullHeight, Footer } from '@client/components/connect-container';
import { parseErrorMessage, UserError } from '@client/config/errors';
import { trackEvent, TrackEvents } from '@client/utils/analytics';
import { ty } from '@client/styles/typography';
import {
  BadgeWrapper,
  ProviderContentWrapper,
  StyledSpan,
  EmptyState,
} from './provider-form.style';
import { ProviderCombobox } from './provider-combobox';
import { ProviderHeader } from './provider-header';

export type Props = {
  submissionError?: unknown;
  pending: boolean;
  initialProviderSearch?: string;
  organization: Organization;
  onSubmit: ({
    provider,
    providerSearchString,
  }: {
    provider: Provider;
    providerSearchString: string;
  }) => void;
  apiMode?: ApiMode;
  usecasesLimit?: UseCase;
};

const searchStringValid = (searchString: string) => searchString.length >= 3;

export const ProviderForm = ({
  submissionError,
  pending,
  onSubmit,
  initialProviderSearch,
  organization,
  apiMode = 'live',
  usecasesLimit,
}: Props): JSX.Element => {
  const { t } = useTranslation('connect', { keyPrefix: 'components.providerForm' });
  const [provider, setProvider] = useState<Provider | undefined>();
  const [noProviderError, setNoProviderError] = useState<UserError | undefined>();
  const [providerSearchString, setProviderSearchString] = useState(
    () => initialProviderSearch ?? ''
  );

  const handleSubmit = () => {
    if (!provider || !providerSearchString) {
      setNoProviderError(new UserError('noProviderError'));
      return;
    }
    trackEvent(TrackEvents.PROVIDER_SELECTED, {
      providerId: provider.id,
      providerName: provider.name,
    });
    onSubmit({ provider, providerSearchString });
  };

  const canSearch = searchStringValid(providerSearchString);

  const { data, isLoading, error } = trpc.provider.search.useQuery(
    { name: providerSearchString, apiMode, usecasesLimit },
    {
      enabled: canSearch,
    }
  );

  const handleSearch = useCallback((searchString: string) => {
    setProviderSearchString(searchString);
    trackEvent(TrackEvents.PROVIDER_SEARCHED, { searchString });
    setProvider(undefined);
    setNoProviderError(undefined);
  }, []);

  const providers = useMemo(() => {
    return data?.providers ?? [];
  }, [data]);

  const handleProviderSelection = useCallback((selectedProvider: Provider | undefined) => {
    setProvider(selectedProvider);
    if (selectedProvider) setNoProviderError(undefined);
  }, []);

  // TO DO: update the parseErrorMessage function to actually respond to these errors
  // right now it only works for noProviderError
  const providerError = noProviderError || submissionError || error;
  const errorMessage = providerError instanceof Error ? parseErrorMessage(providerError) : '';

  return (
    <>
      <ContentSectionFullHeight withTopPadding={false}>
        <ProviderHeader organization={organization} />
        <Form onSubmit={handleSubmit} id="providerForm">
          <ProviderCombobox
            providers={providers}
            selectedProvider={provider}
            onSelect={handleProviderSelection}
            onSearch={handleSearch}
            initialSearchInput={initialProviderSearch}
            label={t('searchLabel')}
            description={t('searchDescription')}
            name="provider"
            errorText={errorMessage}
            autoFocus={true}
          />
        </Form>
        <ProviderContentWrapper errorPresent={!!(providerError instanceof Error)}>
          {canSearch && isLoading ? (
            <Spinner className="mx-auto size-6" data-testid="loading" />
          ) : null}
          {!!(canSearch && !providers.length && !isLoading) && (
            <EmptyState>
              <p className={ty('default')}>
                <Trans t={t} i18nKey="noProvidersText">
                  <StyledSpan>{{ providerSearchString } as any}</StyledSpan>
                </Trans>
              </p>
              <p className={ty('default')}>{t('refineSearchText')}</p>
            </EmptyState>
          )}
        </ProviderContentWrapper>
        <BadgeWrapper>
          <SVG src={arcadiaBadge} />
        </BadgeWrapper>
      </ContentSectionFullHeight>
      <Footer>
        <Button
          form="providerForm"
          aria-label={t('continueButton')}
          type="submit"
          loading={pending}
          className="w-full"
          variant="inverse"
        >
          {t('continueButton')}
        </Button>
      </Footer>
    </>
  );
};
